"use client"

import { Card, Badge, Button } from "react-bootstrap"
import moment from "moment"
import { useState } from "react"

const PlantingCalendar = ({ crops }) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  const colors = ["success", "primary", "info", "warning", "danger", "secondary", "dark", "success", "primary", "info"]

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  if (!crops || crops.length === 0) {
    return (
      <div className="text-center py-4 text-muted">
        <p>No crops to display in calendar</p>
      </div>
    )
  }

  const totalPages = Math.ceil(crops.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentCrops = crops.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const getCropMonths = (crop) => {
    const startMonth = moment(crop.startDate).month()
    const endMonth = moment(crop.endDate).month()
    const months = []

    let current = startMonth
    while (current !== endMonth) {
      months.push(current)
      current = (current + 1) % 12
    }
    months.push(endMonth)

    return months
  }

  return (
    <div>
      <div className="mb-3">
        <small className="text-muted">
          Visual timeline showing optimal planting windows for each suitable crop. Colored months indicate the growing
          season for each crop.
          {totalPages > 1 && ` Showing ${startIndex + 1}-${Math.min(endIndex, crops.length)} of ${crops.length} crops.`}
        </small>
      </div>

      <div className="row g-3">
        {currentCrops.map((crop, cropIndex) => (
          <div key={cropIndex} className="col-12">
            <Card className="border-0 shadow-sm">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="mb-1 fw-bold">{crop.name}</h6>
                    <small className="text-muted">
                      {crop.growth_period_days} days •{moment(crop.startDate).format("MMM DD")} -{" "}
                      {moment(crop.endDate).format("MMM DD")}
                    </small>
                  </div>
                  <Badge bg={colors[(startIndex + cropIndex) % colors.length]} className="rounded-pill">
                    {crop.suitabilityScore || 85}%
                  </Badge>
                </div>

                <div className="row g-1">
                  {months.map((month, monthIndex) => {
                    const isActive = getCropMonths(crop).includes(monthIndex)
                    return (
                      <div key={monthIndex} className="col">
                        <div
                          className={`
                            text-center py-2 rounded small fw-medium
                            ${isActive ? `bg-${colors[(startIndex + cropIndex) % colors.length]} text-white` : "bg-light text-muted"}
                          `}
                          style={{ fontSize: "0.75rem" }}
                        >
                          {month}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <div className="d-flex gap-1">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ Previous
            </Button>

            {[...Array(totalPages)].map((_, i) => (
              <Button
                key={i + 1}
                variant={currentPage === i + 1 ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Button>
            ))}

            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next ›
            </Button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 p-3 bg-light rounded">
        <h6 className="mb-2">📖 Legend</h6>
        <div className="row">
          <div className="col-md-6">
            <small>
              <span className="badge bg-success me-2">Colored</span>
              Optimal growing period
            </small>
          </div>
          <div className="col-md-6">
            <small>
              <span className="badge bg-light text-dark me-2">Gray</span>
              Not suitable for growing
            </small>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlantingCalendar
